import { create } from 'zustand'
import { persist } from 'zustand/middleware'
import {
  Achievement,
  ACHIEVEMENTS,
  checkRequirement,
  getStatForRequirement,
  GameStats,
  initialStats
} from '@/config/achievementsConfig'

interface AchievementsState {
  unlockedAchievements: string[]
  stats: GameStats
  
  // Actions
  unlockAchievement: (id: string) => boolean
  isUnlocked: (id: string) => boolean
  updateStat: (key: keyof GameStats, value: number) => void
  incrementStat: (key: keyof GameStats, amount?: number) => void
  checkAchievements: () => Achievement[]
  getProgress: (achievement: Achievement) => number
  reset: () => void
}

export const useAchievementsStore = create<AchievementsState>()(
  persist(
    (set, get) => ({
      unlockedAchievements: [],
      stats: initialStats,
      
      unlockAchievement: (id: string) => {
        const { unlockedAchievements, isUnlocked } = get()
        if (isUnlocked(id)) return false
        
        set({ unlockedAchievements: [...unlockedAchievements, id] })
        return true
      },
      
      isUnlocked: (id: string) => {
        return get().unlockedAchievements.includes(id)
      },
      
      updateStat: (key, value) => {
        const { stats } = get()
        set({ stats: { ...stats, [key]: value } })
      },
      
      incrementStat: (key, amount = 1) => {
        const { stats } = get()
        const currentValue = stats[key] as number
        set({ stats: { ...stats, [key]: currentValue + amount } })
      },
      
      checkAchievements: () => {
        const { stats, isUnlocked, unlockAchievement } = get()
        const newlyUnlocked: Achievement[] = []
        
        for (const achievement of ACHIEVEMENTS) {
          if (!isUnlocked(achievement.id)) {
            if (checkRequirement(achievement.requirement, stats)) {
              if (unlockAchievement(achievement.id)) {
                newlyUnlocked.push(achievement)
              }
            }
          }
        }
        
        return newlyUnlocked
      },
      
      getProgress: (achievement: Achievement) => {
        const { stats } = get()
        const req = achievement.requirement
        const current = getStatForRequirement(req.type, stats)
        return Math.min(100, (current / req.value) * 100)
      },
      
      reset: () => set({ unlockedAchievements: [], stats: initialStats }),
    }),
    {
      name: 'padel-achievements',
    }
  )
)
